import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Video Editing",
  "Social Media Management",
  "Thumbnail & Cover Design",
  "AI Automation",
  "Full Agency Package",
  "Custom Service",
];

const Bold = ({ children }) => (
  <span className="font-semibold text-[#0a1d4d]">{children}</span>
);

const DEFAULT_NOTE = (
  <>
    Minimum budget <Bold>$200</Bold> — we don’t take projects below that.
  </>
);

/* Per-service budget behavior: min = enforced floor, prefill = auto-set value,
   showMessage = reveal requirements textarea */
const SERVICE_RULES = {
  "Video Editing": { note: <>Pricing depends on your requirements.</> },
  "Social Media Management": {
    min: 1200,
    note: (
      <>
        Minimum budget <Bold>$1,200/month</Bold> — we don’t take projects
        below that.
      </>
    ),
  },
  "Thumbnail & Cover Design": {
    note: <>Pricing depends on your requirements.</>,
  },
  "AI Automation": {
    min: 2000,
    note: (
      <>
        Minimum budget <Bold>$2,000</Bold> — we don’t take projects below
        that.
      </>
    ),
  },
  "Full Agency Package": {
    min: 3999,
    prefill: "$3,999",
    note: (
      <>
        Starting price <Bold>$3,999</Bold> — applied automatically.
      </>
    ),
  },
  "Custom Service": {
    showMessage: true,
    note: (
      <>
        Describe your requirements in your message, and we’ll create a custom
        solution for your business.
      </>
    ),
  },
};

const parseBudget = (str) => {
  const m = (str || "").replace(/,/g, "").match(/\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : NaN;
};

const ContactDialog = ({ open, onOpenChange, defaultService = "" }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    service: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const activeRule = SERVICE_RULES[form.service] || { note: DEFAULT_NOTE };

  useEffect(() => {
    if (defaultService) {
      setForm((f) => ({ ...f, service: defaultService }));
    }
  }, [defaultService]);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.whatsapp.trim()) {
      toast.error("Please fill in your name, email and WhatsApp number.");
      return;
    }
    if (activeRule.min) {
      const amount = parseBudget(form.budget);
      if (isNaN(amount) || amount < activeRule.min) {
        toast.error(
          `Minimum budget for ${form.service} is $${activeRule.min.toLocaleString()}.`
        );
        return;
      }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
        service: form.service || null,
        budget: form.budget.trim() || null,
        message: form.message.trim() || null,
      });
      toast.success("Brief received! We’ll reply within 24 hours.");
      setForm({
        name: "",
        email: "",
        whatsapp: "",
        service: "",
        budget: "",
        message: "",
      });
      onOpenChange(false);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl p-0 overflow-hidden border-[#0a1d4d]/10 rounded-3xl [&>button]:hidden"
        data-testid="contact-dialog"
      >
        <div className="relative bg-[#0a1d4d] text-white p-6 sm:p-8">
          <div
            className="lq-orb"
            style={{
              background: "#1d72ff",
              width: 260,
              height: 260,
              top: -100,
              right: -60,
              opacity: 0.4,
            }}
          />
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="contact-dialog-close"
          >
            <X className="w-4 h-4" />
          </button>
          <DialogHeader className="relative space-y-2 text-left">
            <DialogTitle className="font-display font-extrabold text-3xl text-white">
              Let’s make you viral.
            </DialogTitle>
            <DialogDescription className="text-white/70 text-sm">
              Tell us a bit about your brand. We’ll reply within 24 hours
              with a custom plan.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="bg-white p-6 sm:p-8 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-[#0a1d4d] font-medium">
                Your name
              </Label>
              <Input
                id="name"
                value={form.name}
                onChange={update("name")}
                placeholder="Alex Carter"
                className="h-11 rounded-xl border-[#0a1d4d]/15 focus-visible:ring-[#1d72ff]"
                data-testid="contact-name-input"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[#0a1d4d] font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="alex@brand.com"
                className="h-11 rounded-xl border-[#0a1d4d]/15 focus-visible:ring-[#1d72ff]"
                data-testid="contact-email-input"
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="whatsapp" className="text-[#0a1d4d] font-medium">
              WhatsApp number
            </Label>
            <Input
              id="whatsapp"
              type="tel"
              value={form.whatsapp}
              onChange={update("whatsapp")}
              placeholder="+91 98765 43210"
              className="h-11 rounded-xl border-[#0a1d4d]/15 focus-visible:ring-[#1d72ff]"
              data-testid="contact-whatsapp-input"
              required
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[#0a1d4d] font-medium">
                Service interested in
              </Label>
              <Select
                value={form.service}
                onValueChange={(v) =>
                  setForm((f) => ({
                    ...f,
                    service: v,
                    budget: SERVICE_RULES[v]?.prefill ?? f.budget,
                  }))
                }
              >
                <SelectTrigger
                  className="h-11 rounded-xl border-[#0a1d4d]/15 focus:ring-[#1d72ff]"
                  data-testid="contact-service-select"
                >
                  <SelectValue placeholder="Pick a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="budget" className="text-[#0a1d4d] font-medium">
                Budget{" "}
                <span className="text-[#0a1d4d]/40 font-normal">
                  (custom plan)
                </span>
              </Label>
              <Input
                id="budget"
                value={form.budget}
                onChange={update("budget")}
                placeholder="e.g. $500/month"
                className="h-11 rounded-xl border-[#0a1d4d]/15 focus-visible:ring-[#1d72ff]"
                data-testid="contact-budget-input"
              />
            </div>
          </div>

          <p
            className="text-xs text-[#0a1d4d]/55 -mt-1"
            data-testid="contact-budget-note"
          >
            {activeRule.note}
          </p>

          {activeRule.showMessage && (
            <div className="space-y-1.5">
              <Label htmlFor="message" className="text-[#0a1d4d] font-medium">
                Your requirements
              </Label>
              <Textarea
                id="message"
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us what you need — goals, platforms, volume, timelines…"
                className="min-h-[110px] rounded-xl border-[#0a1d4d]/15 focus-visible:ring-[#1d72ff]"
                data-testid="contact-message-input"
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#1d72ff] hover:bg-[#0a1d4d] text-white font-semibold transition-colors group"
            data-testid="contact-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sending…
              </>
            ) : (
              <>
                Send brief
                <Send className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </Button>
          <p className="text-center text-xs text-[#0a1d4d]/50">
            By submitting you agree to our placeholder privacy policy. We
            never spam.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactDialog;
